import { escapeHtml } from '../util/escapeHtml.js';
import { api, HttpError } from '../api/client.js';
import { store } from '../state/store.js';
import { navigate } from '../router.js';

/**
 * Email verification landing page. The URL is the link emailed at registration
 * (or when the user clicks "Resend verification" later). On mount we read the
 * token from ?token=... and immediately POST it; the user only sees a status
 * card.
 */
export async function renderVerifyEmail(root: HTMLElement): Promise<void> {
  const params = new URLSearchParams(window.location.search);
  let token = params.get('token') ?? '';
  if (!token && window.location.hash.includes('?')) {
    const after = window.location.hash.split('?')[1] ?? '';
    token = new URLSearchParams(after).get('token') ?? '';
  }

  root.innerHTML = `
    <div class="auth-shell">
      <div class="auth-card" id="card">
        <img src="/img/ui/title.webp" alt="Rex Mortis" style="display:block; margin: 0 auto var(--s-3) auto; width: 280px; max-width: 90%; height: auto;" />
        <h1 style="text-align:center; margin: 0 0 var(--s-1) 0;">Verifying thy raven's nest…</h1>
        <p style="text-align:center; font-style:italic; color: var(--c-ink-soft);">A moment, while the Heralds check the seal.</p>
      </div>
    </div>
  `;

  const card = root.querySelector<HTMLDivElement>('#card')!;

  if (!token) {
    renderResult(card, {
      ok: false,
      title: 'Missing verification token',
      body: 'This verification scroll has no token — perhaps the link was copied incompletely.',
    });
    return;
  }

  try {
    const r = await api.verifyEmail(token);
    // If the signed-in account matches this verification, flip the local flag
    // so the banner disappears without needing a full page reload.
    const s = store.get();
    if (s.account && s.account.email === r.email) {
      store.set({ account: { ...s.account, emailVerified: true } });
    }
    renderResult(card, {
      ok: true,
      title: 'Email confirmed',
      body: `The Heralds may now reach <strong>${escapeHtml(r.email)}</strong>. Welcome to Rex Mortis in earnest.`,
      cta: s.accessToken ? { label: 'To my profile', href: '#/profile' } : { label: 'To the gate', href: '#/login' },
    });
  } catch (err) {
    const msg = err instanceof HttpError ? err.message : 'Could not verify the link.';
    renderResult(card, {
      ok: false,
      title: 'Verification failed',
      body: msg,
      cta: store.get().accessToken
        ? { label: 'Request a new link', href: '#/settings' }
        : { label: 'Return to the gate', href: '#/login' },
    });
  }
}

function renderResult(
  card: HTMLDivElement,
  args: { ok: boolean; title: string; body: string; cta?: { label: string; href: string } },
): void {
  card.innerHTML = `
    <img src="/img/ui/title.webp" alt="Rex Mortis" style="display:block; margin: 0 auto var(--s-3) auto; width: 280px; max-width: 90%; height: auto;" />
    <h1 style="text-align:center; margin: 0 0 var(--s-2) 0;">${escapeHtml(args.title)}</h1>
    <div class="alert ${args.ok ? 'success' : 'danger'}" style="text-align:center;">${args.body}</div>
    ${args.cta ? `<p class="auth-meta" style="text-align:center;"><a href="${args.cta.href}" class="btn" style="display:inline-block; margin-top: var(--s-2);">${escapeHtml(args.cta.label)}</a></p>` : ''}
  `;
}

// silence unused-import warning when navigate isn't reached
void navigate;
